import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, RefreshCw, KeyRound, Clock, Copy, Crown, User as UserIcon, ShieldCheck, Users2, Mail, MailX, MailCheck, Send, Smartphone, Eye, EyeOff, RotateCcw } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function formatWhen(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

function minutesLeft(iso) {
  if (!iso) return null;
  const diff = (new Date(iso).getTime() - Date.now()) / 60000;
  return Math.max(0, Math.floor(diff));
}

export default function PendingCodes() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isAdmin = user?.role === "MASTER_ADMIN";
  const ROLE_BADGES = {
    MASTER_AGENT:  { label: t("role.MASTER_AGENT"),  cls: "bg-violet-50 text-violet-700 border-violet-200",    Icon: Crown },
    AGENT:         { label: t("role.AGENT"),         cls: "bg-slate-100 text-slate-700 border-slate-200",       Icon: UserIcon },
    CASH_VERIFIER: { label: t("role.CASH_VERIFIER"), cls: "bg-emerald-50 text-emerald-700 border-emerald-200",  Icon: ShieldCheck },
    COLLABORATOR:  { label: "Collaborator",           cls: "bg-amber-50 text-amber-800 border-amber-200",        Icon: Users2 },
  };
  const FILTERS = [
    { id: "ALL",           label: t("pending_codes.filter_all") },
    { id: "AGENT",         label: t("pending_codes.filter_agents") },
    { id: "MASTER_AGENT",  label: t("pending_codes.filter_masters") },
    // Collaborator tab is admin-only (MASTER_AGENT never sees these rows).
    ...(isAdmin ? [{ id: "COLLABORATOR", label: "Collaborators" }] : []),
    { id: "CASH_VERIFIER", label: t("pending_codes.filter_verifiers") },
    { id: "moncash",       label: t("pending_codes.filter_moncash") },
    { id: "natcash",       label: t("pending_codes.filter_natcash") },
  ];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(null); // id being resent
  const [approving, setApproving] = useState(null); // id being approved
  const [revealing, setRevealing] = useState(null); // id being revealed
  const [refreshingStatus, setRefreshingStatus] = useState(null); // id being status-refreshed
  // { [otpId]: { code: "123456", secondsLeft: 20 } }
  const [revealed, setRevealed] = useState({});
  const revealTimers = useRef({});
  const [filter, setFilter] = useState("ALL");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pending-codes"); // unified — returns all visible roles
      setItems(data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("pending_codes.failed_load"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "ALL") return items;
    if (filter === "moncash" || filter === "natcash") {
      return items.filter((r) => r.role === "CASH_VERIFIER" && (r.provider || "").toLowerCase() === filter);
    }
    return items.filter((r) => r.role === filter);
  }, [items, filter]);

  const counts = useMemo(() => ({
    ALL: items.length,
    MASTER_AGENT: items.filter((r) => r.role === "MASTER_AGENT").length,
    AGENT: items.filter((r) => r.role === "AGENT").length,
    COLLABORATOR: items.filter((r) => r.role === "COLLABORATOR").length,
    CASH_VERIFIER: items.filter((r) => r.role === "CASH_VERIFIER").length,
    moncash: items.filter((r) => r.role === "CASH_VERIFIER" && (r.provider || "").toLowerCase() === "moncash").length,
    natcash: items.filter((r) => r.role === "CASH_VERIFIER" && (r.provider || "").toLowerCase() === "natcash").length,
  }), [items]);

  const copyCode = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t("pending_codes.code_copied"));
    } catch {
      toast(`Code: ${code}`);
    }
  };

  const resendEmail = async (row) => {
    setResending(row.id);
    try {
      const { data } = await api.post(`/pending-codes/${row.id}/resend-email`);
      if (data.sent) {
        toast.success("Verification code re-sent to Master Admin.");
      } else {
        toast.error(`Email delivery failed: ${data.delivery || "unknown error"}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resend failed.");
    } finally {
      setResending(null);
    }
  };

  const approveDevice = async (row) => {
    // Prompt for the approver's own password to gate the action.
    // Simple prompt() is deliberate — this is an emergency recovery flow,
    // not a daily action, so a modal would be over-engineered for now.
    const pwd = window.prompt(
      `Approve login device for ${row.name || row.username} (${row.role})?\n\n` +
      `This grants immediate access from ${row.phone_masked || "the requesting device"}.\n\n` +
      `Enter YOUR password to confirm:`,
    );
    if (!pwd) return;
    setApproving(row.id);
    try {
      const { data } = await api.post(`/pending-codes/${row.id}/approve-device`, { password: pwd, trust_device: true });
      if (data.ok) {
        toast.success(`Device approved — the user can now sign in.`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Approval failed.");
    } finally {
      setApproving(null);
    }
  };

  // ---------------------------------------------------------------------
  // Start Assisted Verification (Phase 3)
  //
  // Generates a ONE-TIME 6-digit assistance code, separate from the real
  // Twilio OTP. The clear-text is returned by the backend ONCE — we
  // display it inside a modal-style prompt so the admin can dictate it
  // to the user over a trusted voice channel. Backend requires:
  //   * admin re-enters their own password
  //   * admin explicitly attests they verified the user's identity
  //   * hierarchy match (same as Approve Device)
  // ---------------------------------------------------------------------
  const [assistBusy, setAssistBusy] = useState(null);
  const [assistCode, setAssistCode] = useState(null); // {code, expires_at, target_username, target_masked_phone}

  const startAssistedVerification = async (row) => {
    const targetLabel = row.name || row.username;
    const confirm1 = window.confirm(
      `Start ASSISTED VERIFICATION for ${targetLabel} (${row.role})?\n\n` +
      `You must have already identified this user through a trusted channel ` +
      `(a live phone call to the number on file, an in-person meeting, or a ` +
      `verified video call).\n\n` +
      `A one-time 6-digit assistance code will be generated. It is separate ` +
      `from the Twilio SMS OTP, valid for a few minutes, single-use.\n\n` +
      `Do you confirm you have verified this user's identity?`,
    );
    if (!confirm1) return;
    const reason = window.prompt(
      "Optional — briefly note the reason (audit log). Leave empty to continue.",
      "User cannot receive SMS",
    );
    if (reason === null) return; // Cancel
    const pwd = window.prompt(
      "Enter YOUR password to authorise the assistance code:",
    );
    if (!pwd) return;
    setAssistBusy(row.id);
    try {
      const { data } = await api.post(
        `/pending-codes/${row.id}/start-assisted-verification`,
        { password: pwd, identity_verified: true, reason: reason.trim(), ttl_seconds: 300 },
      );
      setAssistCode({
        code: data.assist_code,
        expires_at: data.expires_at,
        ttl_seconds: data.ttl_seconds,
        target_username: row.username,
        target_masked_phone: data.target_masked_phone || row.phone_masked,
      });
      toast.success(
        `Assistance code ready — dictate it to ${targetLabel} via a trusted call.`,
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start assisted verification.");
    } finally {
      setAssistBusy(null);
    }
  };

  // Reveal Code — the OTP is decrypted server-side, returned once, and
  // auto-hidden after 20 seconds (spec §K). Requires re-authentication and
  // hierarchy validation on the backend.
  const clearRevealTimer = (id) => {
    if (revealTimers.current[id]) {
      clearInterval(revealTimers.current[id]);
      delete revealTimers.current[id];
    }
  };
  const hideRevealed = (id) => {
    clearRevealTimer(id);
    setRevealed((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };
  useEffect(() => () => {
    // Clean up any active timers on unmount.
    Object.keys(revealTimers.current).forEach(clearRevealTimer);
  }, []);

  const revealCode = async (row) => {
    const pwd = window.prompt(
      `Reveal login code for ${row.name || row.username} (${row.role})?\n\n` +
      `The code will be shown for 20 seconds and this reveal will be recorded in the audit log.\n\n` +
      `Enter YOUR password to confirm:`,
    );
    if (!pwd) return;
    setRevealing(row.id);
    try {
      const { data } = await api.post(`/pending-codes/${row.id}/reveal`, { password: pwd });
      const seconds = data.display_seconds || 20;
      clearRevealTimer(row.id);
      setRevealed((prev) => ({ ...prev, [row.id]: { code: data.otp, secondsLeft: seconds } }));
      revealTimers.current[row.id] = setInterval(() => {
        setRevealed((prev) => {
          const cur = prev[row.id];
          if (!cur) return prev;
          if (cur.secondsLeft <= 1) {
            clearRevealTimer(row.id);
            const next = { ...prev };
            delete next[row.id];
            return next;
          }
          return { ...prev, [row.id]: { ...cur, secondsLeft: cur.secondsLeft - 1 } };
        });
      }, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reveal failed.");
    } finally {
      setRevealing(null);
    }
  };

  // Fetch the latest Twilio delivery status for this challenge — surfaces
  // Twilio error codes (e.g. 21704 empty Sender Pool, 30003 unreachable,
  // 30006 landline / not SMS-capable, etc.) that only appear AFTER the
  // initial API request was accepted.
  const refreshDeliveryStatus = async (row) => {
    setRefreshingStatus(row.id);
    try {
      const { data } = await api.post(`/pending-codes/${row.id}/refresh-status`);
      if (data.sms_status === "SMS_DELIVERED") {
        toast.success("SMS delivered.");
      } else if (data.sms_status === "SMS_FAILED") {
        toast.error(
          `SMS delivery failed. Twilio error ${data.error_code || "?"}: ${data.error_message || "no details"}`
        );
      } else {
        toast(`Delivery still pending — Twilio status: ${data.twilio_status || "unknown"}`);
      }
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Refresh failed.");
    } finally {
      setRefreshingStatus(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="pending-codes-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
            <KeyRound className="w-3.5 h-3.5 text-slate-500" /> {t("pending_codes.breadcrumb")}
          </div>
          <h1
            className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            {t("pending_codes.title")}
          </h1>
          <p className="text-slate-500 text-sm mt-1 max-w-3xl">
            {t("pending_codes.long_subtitle")}
          </p>
        </div>
        <button
          onClick={load}
          className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-300 text-slate-700 text-xs font-semibold hover:bg-slate-50"
          data-testid="pending-reload"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {t("pending_codes.reload")}
        </button>
      </div>

      {/* Role filter tabs — always show; MA users just won't see MASTER_AGENT rows. */}
      <div className="flex flex-wrap gap-2" data-testid="pending-codes-filter">
        {FILTERS.map((f) => {
          const active = filter === f.id;
          return (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`pending-filter-${f.id.toLowerCase()}`}
              className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-xs font-semibold border transition-colors ${
                active
                  ? "bg-[#0F62FE] text-white border-[#0F62FE]"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
              }`}
            >
              {f.label}
              <span className={`ml-1 px-1.5 py-0.5 rounded text-[10px] font-bold ${active ? "bg-white/20" : "bg-slate-100 text-slate-600"}`}>
                {counts[f.id] || 0}
              </span>
            </button>
          );
        })}
      </div>

      <div className="bg-white rounded-md border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pending-codes-table">
            <thead>
              <tr>
                {[t("pending_codes.col_role"), t("pending_codes.col_user"), t("pending_codes.col_username"), t("pending_codes.col_email"), t("pending_codes.col_code"), t("pending_codes.col_created"), t("pending_codes.col_expires"), t("pending_codes.col_status")].map((h) => (
                  <th key={h} className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap">
                    {h}
                  </th>
                ))}
                {isAdmin && (
                  <th className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap">
                    Delivery
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isAdmin ? 9 : 8} className="px-5 py-10 text-center text-slate-500 text-sm">
                  <Loader2 className="w-5 h-5 animate-spin inline-block mr-2" /> {t("pending_codes.loading")}
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={isAdmin ? 9 : 8} className="px-5 py-10 text-center text-slate-500 text-sm">
                  {t("pending_codes.no_active")}
                </td></tr>
              )}
              {!loading && filtered.map((r) => {
                const mLeft = minutesLeft(r.expires_at);
                const badge = ROLE_BADGES[r.role] || { label: r.role || "—", cls: "bg-slate-100 text-slate-700 border-slate-200", Icon: UserIcon };
                const Icon = badge.Icon;
                const isCollab = r.role === "COLLABORATOR";
                const delivery = (r.email_delivery_status || "").toUpperCase();
                return (
                  <tr key={r.id} className="border-b border-slate-100" data-testid={`code-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest px-2 py-1 rounded border ${badge.cls}`}>
                        <Icon className="w-3 h-3" strokeWidth={2.5} />
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-900 font-semibold">{r.name || r.username || r.user_id}</td>
                    <td className="px-4 py-3 text-slate-700 font-mono text-xs">{r.username}</td>
                    <td className="px-4 py-3 text-slate-500 text-xs">{r.email || "—"}</td>
                    <td className="px-4 py-3">
                      {revealed[r.id] ? (
                        <div className="flex flex-col gap-1" data-testid={`code-revealed-${r.id}`}>
                          <div className="inline-flex items-center gap-2">
                            <button
                              onClick={() => copyCode(revealed[r.id].code)}
                              className="inline-flex items-center gap-1.5 font-mono text-lg font-bold text-emerald-700 hover:text-emerald-800 tracking-[4px]"
                              data-testid={`code-copy-${r.id}`}
                              title="Copy code"
                            >
                              {revealed[r.id].code}
                              <Copy className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                            <button
                              onClick={() => hideRevealed(r.id)}
                              data-testid={`code-hide-${r.id}`}
                              className="text-[11px] text-slate-500 hover:text-slate-800"
                              title="Hide code now"
                            >
                              <EyeOff className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <span className="text-[10px] font-semibold uppercase tracking-widest text-emerald-700">
                            Auto-hide in {revealed[r.id].secondsLeft}s
                          </span>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-1" data-testid={`code-sms-cell-${r.id}`}>
                          <span className="inline-flex items-center gap-1.5 font-mono text-lg font-bold text-slate-400 tracking-[6px]" data-testid={`code-masked-${r.id}`}>
                            ••••••
                          </span>
                          <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-500">
                            <Smartphone className="w-3 h-3 text-slate-400" />
                            {r.phone_masked || "—"}
                          </span>
                          <div className="flex items-center gap-3 mt-0.5">
                            <button
                              onClick={() => revealCode(r)}
                              disabled={revealing === r.id}
                              data-testid={`code-reveal-${r.id}`}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0F62FE] hover:text-[#0050E6] disabled:opacity-50"
                              title="Show code for 20 seconds (audited)"
                            >
                              {revealing === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Eye className="w-3 h-3" />}
                              Reveal Code
                            </button>
                            {/* Approve Device & Start Assisted Verification.
                                Both are gated by hierarchy on the backend so
                                MASTER_AGENT can help their own agents without
                                any extra client-side role check. */}
                            <button
                              onClick={() => approveDevice(r)}
                              disabled={approving === r.id}
                              data-testid={`code-approve-${r.id}`}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 hover:text-emerald-800 disabled:opacity-50"
                            >
                              {approving === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <ShieldCheck className="w-3 h-3" />}
                              {t("pending_codes.approve_device", { defaultValue: "Approve Device" })}
                            </button>
                            <button
                              onClick={() => startAssistedVerification(r)}
                              disabled={assistBusy === r.id || r.assist_code_active}
                              data-testid={`code-assist-${r.id}`}
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-indigo-700 hover:text-indigo-800 disabled:opacity-50"
                              title={r.assist_code_active
                                ? "An assistance code is already active for this challenge"
                                : "Generate a one-time assistance code you can dictate over a trusted channel"}
                            >
                              {assistBusy === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <KeyRound className="w-3 h-3" />}
                              {r.assist_code_active
                                ? t("pending_codes.assist_active", { defaultValue: "Assist Active" })
                                : t("pending_codes.start_assisted", { defaultValue: "Start Assisted Verification" })}
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">{formatWhen(r.created_at)}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        {t("pending_codes.min_left", { n: mLeft })}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                        {r.status}
                      </span>
                    </td>
                    {isAdmin && (
                      <td className="px-4 py-3" data-testid={`code-delivery-${r.id}`}>
                        {(r.channel === "sms" || isCollab) ? (
                          <div className="flex flex-col gap-1.5">
                            {(delivery === "DELIVERED" || delivery === "SMS_DELIVERED") && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">
                                <MailCheck className="w-3 h-3" /> SMS Delivered
                              </span>
                            )}
                            {(delivery === "SENT" || delivery === "SMS_ACCEPTED" || delivery === "SMS_SENT" || delivery === "SENDING") && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-sky-50 text-sky-700 border-sky-200"
                                title="Twilio accepted the request — delivery not yet confirmed. Click Refresh Status to update."
                              >
                                <MailCheck className="w-3 h-3" /> Accepted — pending
                              </span>
                            )}
                            {(delivery === "FAILED" || delivery === "SMS_FAILED" || delivery === "UNDELIVERED" || delivery === "SMS_UNDELIVERED") && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-red-50 text-red-700 border-red-200"
                                title={r.sms_error_message || r.sms_error || r.email_delivery_error || ""}
                              >
                                <MailX className="w-3 h-3" />
                                SMS Failed{r.sms_error_code ? ` (${r.sms_error_code})` : ""}
                              </span>
                            )}
                            {r.sms_error_code && (delivery === "FAILED" || delivery === "SMS_FAILED" || delivery === "UNDELIVERED" || delivery === "SMS_UNDELIVERED") && (
                              <span className="text-[10px] text-red-700 max-w-[220px] leading-tight" title={r.sms_error_message}>
                                {r.sms_error_message || "See Twilio Console for details."}
                              </span>
                            )}
                            {!delivery && (
                              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded border bg-slate-50 text-slate-700 border-slate-200">
                                <Mail className="w-3 h-3" /> Pending
                              </span>
                            )}
                            <div className="inline-flex items-center gap-3">
                              <button
                                onClick={() => refreshDeliveryStatus(r)}
                                disabled={refreshingStatus === r.id}
                                data-testid={`code-refresh-status-${r.id}`}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-50"
                                title="Fetch the real delivery status from Twilio"
                              >
                                {refreshingStatus === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                                Refresh
                              </button>
                              <button
                                onClick={() => resendEmail(r)}
                                disabled={resending === r.id}
                                data-testid={`code-resend-${r.id}`}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0F62FE] hover:text-[#0050E6] disabled:opacity-50"
                              >
                                {resending === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                Resend SMS
                              </button>
                            </div>
                          </div>
                        ) : (
                          <span className="text-[11px] text-slate-400">—</span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[11px] text-slate-500 leading-relaxed">
        <b>{t("pending_codes.escalation_path")}</b> {t("pending_codes.escalation_text")}
      </div>

      {/* Assist-code display modal — the clear-text code is returned by
          the backend ONCE and lives only in this state. Closing the modal
          discards it from memory. */}
      {assistCode && (
        <div
          data-testid="assist-modal-backdrop"
          className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4"
          onClick={() => setAssistCode(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="assist-modal"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <KeyRound className="w-5 h-5 text-indigo-700" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest font-semibold text-indigo-600">
                  {t("pending_codes.assist_title_badge", { defaultValue: "Assisted Verification" })}
                </div>
                <div className="text-lg font-bold text-slate-900">
                  {t("pending_codes.assist_dictate", { defaultValue: "Dictate this code to the user" })}
                </div>
              </div>
            </div>
            <div className="text-xs text-slate-600 leading-relaxed mb-3">
              {t("pending_codes.assist_intro", {
                defaultValue:
                  "Communicate the code below to {{user}} via a trusted voice call (not SMS, not chat). It is separate from their Twilio OTP, single-use, and expires in a few minutes.",
                user: assistCode.target_username,
              })}
            </div>
            <div
              className="rounded-xl border-2 border-indigo-300 bg-indigo-50 p-4 mb-3 text-center"
              data-testid="assist-code-display"
            >
              <div className="font-mono text-4xl font-black text-indigo-900 tracking-widest">
                {assistCode.code}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-indigo-700 mt-2">
                {t("pending_codes.assist_expires_at", { defaultValue: "Expires" })}: {formatWhen(assistCode.expires_at)}
              </div>
            </div>
            <div className="text-[11px] text-slate-500 mb-4">
              {t("pending_codes.assist_target_phone", { defaultValue: "Trusted number on file" })}:{" "}
              <strong className="text-slate-700 font-mono">{assistCode.target_masked_phone || "—"}</strong>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(assistCode.code);
                  toast.success(t("pending_codes.assist_copied", { defaultValue: "Copied to clipboard" }));
                }}
                data-testid="assist-copy-btn"
                className="flex-1 h-10 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 inline-flex items-center justify-center gap-2"
              >
                <Copy className="w-4 h-4" />
                {t("pending_codes.assist_copy", { defaultValue: "Copy code" })}
              </button>
              <button
                onClick={() => setAssistCode(null)}
                data-testid="assist-close-btn"
                className="flex-1 h-10 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
              >
                {t("pending_codes.assist_close", { defaultValue: "Close" })}
              </button>
            </div>
            <div className="mt-3 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
              {t("pending_codes.assist_warning", {
                defaultValue:
                  "This code will disappear when you close this dialog. It cannot be shown again.",
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
